import type { Writable } from 'type-fest';
import type { Event } from './types';
import { getDefaultEventOptions } from './utils';

declare global {
  interface Window {
    lastTrackParams?: { readonly name: Event['name']; readonly payload: Record<string, unknown> } | null; // eslint-disable-line functional/prefer-readonly-type
  }
}

export function track<TEventName extends Event['name']>(
  name: TEventName,
  payload: Partial<(Event & { readonly name: NoInfer<TEventName> })['payload']>,
): void {
  window.lastTrackParams = { name, payload };

  if (window.analytics?.ready != null && window.analytics.track != null) {
    const defaultOptions = getDefaultEventOptions();
    window.analytics.track(name, payload, defaultOptions);
  }
}

export function trackAppCrash(error: Error | string | null): void {
  try {
    const lastParams = window.lastTrackParams;
    track('chat.crash', {
      ...lastParams?.payload,
      errorMessage: typeof error === 'string' ? error : error?.message,
      errorStack: typeof error === 'string' ? undefined : error?.stack,
    });
  } catch (err) {
    // eslint-disable-next-line no-empty
  }
}

type Traits = Record<string, unknown>;
export function identify(id: string, traits?: Traits): void;
export function identify(traits: Traits): void;
export function identify(id: Traits | string, traits?: Traits): void {
  const isIdString = typeof id === 'string';
  const defaultOptions = getDefaultEventOptions(isIdString ? id : undefined);
  if (isIdString) {
    if (traits == null) {
      window.analytics?.identify?.(id, defaultOptions);
    } else {
      window.analytics?.identify?.(id, traits, defaultOptions);
    }
  } else {
    const newTraits = id;
    window.analytics?.identify?.(newTraits, defaultOptions);
  }
}

export function alias(userId: string, previousId?: string): void {
  if (window.analytics?.ready != null && window.analytics.alias != null) {
    const defaultOptions = getDefaultEventOptions(userId);
    if (previousId == null) {
      window.analytics.alias(userId, defaultOptions);
      return;
    }

    window.analytics.alias(userId, previousId, defaultOptions);
  }
}

// used on user logout
export function reset(): void {
  window.analytics?.reset?.();
}

interface LogRocketSessionUrlTrackerRef {
  readonly current: Record<string, string> | null;
}

export function setSessionRecordingUrlCallback(ref: Writable<LogRocketSessionUrlTrackerRef>): void {
  if (!window._lr_loaded) return;

  ref.current = {};
  window._lr_surl_cb?.((url: string) => {
    if (window.analytics?.ready != null && window.analytics.track != null) {
      ref.current = { logRocketSessionUrl: url };
    }
  });
}
